import {
  Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell,
  WidthType, AlignmentType, ShadingType, VerticalAlign, HeightRule,
} from 'docx';

// 本文フォント（日本語）。Document の styles.default で全体に適用する。
const BODY_FONT = { ascii: 'MS Mincho', eastAsia: 'MS Mincho', hAnsi: 'MS Mincho' };

// ページの印刷可能幅(A4・下記margin時で10091DXA)より、承認印表／会議情報表の幅(9100DXA)が
// 狭いため、テーブルを中央寄せにした後もその左右に余りができる。「報告者・氏名」の行は
// 表と違って通常の段落なので、そのままAlignmentType.RIGHTにすると表の右端ではなく
// ページの右端に揃ってしまう。表の右端と行の終わりが揃って見えるよう、その余りの半分
// (右側の分)だけ段落を右インデントする。
const CONTENT_WIDTH = 10091; // 11906(A4幅) - 964(左余白) - 851(右余白)
const TABLE_WIDTH = 9100;
const TABLE_SIDE_GAP = Math.round((CONTENT_WIDTH - TABLE_WIDTH) / 2);

// 実際に使われている「会議報告書」の様式（承認印欄）を再現したもの。
// 役職名は法人共通で固定。値は無く、印を押すための空欄として使う。
const STAMP_ROW_A = ['施設長', '総務部長', '総務運営推進主任', 'デイ主任相談員', '介護主任', '看護主任', '管理栄養士'];
const STAMP_ROW_B = ['生活相談員', '生活相談員', '介護支援専門員', '小規模管理者', 'DSサイタ管理者', 'ポム管理者', ''];
const STAMP_COL_WIDTH = 1300;
const STAMP_ROW_HEIGHT = 1000; // 印を押すための空欄の高さ(DXA)。従来の目安から倍増。

function stampLabelCell(text) {
  return new TableCell({
    width: { size: STAMP_COL_WIDTH, type: WidthType.DXA },
    verticalAlign: VerticalAlign.CENTER,
    children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text, size: 16 })] })],
  });
}
function stampBlankCell() {
  return new TableCell({
    width: { size: STAMP_COL_WIDTH, type: WidthType.DXA },
    children: [new Paragraph({ children: [new TextRun('')] })],
  });
}
function buildStampTable() {
  return new Table({
    width: { size: STAMP_COL_WIDTH * 7, type: WidthType.DXA },
    columnWidths: new Array(7).fill(STAMP_COL_WIDTH),
    alignment: AlignmentType.CENTER,
    rows: [
      new TableRow({ children: STAMP_ROW_A.map(stampLabelCell) }),
      new TableRow({
        height: { value: STAMP_ROW_HEIGHT, rule: HeightRule.ATLEAST },
        children: STAMP_ROW_A.map(stampBlankCell),
      }),
      new TableRow({ children: STAMP_ROW_B.map(stampLabelCell) }),
      new TableRow({
        height: { value: STAMP_ROW_HEIGHT, rule: HeightRule.ATLEAST },
        children: STAMP_ROW_B.map(stampBlankCell),
      }),
    ],
  });
}

// 実際に使われている様式に合わせた4カラム構成。「進行」「記録」は1行に横並びにする。
const INFO_LABEL_WIDTH = 1800;
const INFO_VALUE_WIDTH = 7300;
const INFO_SPLIT_VALUE_WIDTH = 2800;
const INFO_SPLIT_LABEL_WIDTH = 1700;
const INFO_ROW_HEIGHT = 700; // 枠にゆとりを持たせるための最小行高(DXA)。実際の様式に合わせた高さ。
const INFO_PARTICIPANTS_ROW_HEIGHT = 1400; // 参加者は名前が並び複数行になりやすいため高めに。

function infoLabelCell(text, width) {
  return new TableCell({
    width: { size: width, type: WidthType.DXA },
    shading: { type: ShadingType.CLEAR, fill: 'F2F2F2' },
    verticalAlign: VerticalAlign.CENTER,
    children: [new Paragraph({ children: [new TextRun({ text, bold: true })] })],
  });
}
function infoValueCell(value, width, columnSpan) {
  return new TableCell({
    width: { size: width, type: WidthType.DXA },
    columnSpan,
    verticalAlign: VerticalAlign.CENTER,
    children: [new Paragraph({ children: [new TextRun(value || '')] })],
  });
}
function infoFullRow(label, value, height = INFO_ROW_HEIGHT) {
  return new TableRow({
    height: { value: height, rule: HeightRule.ATLEAST },
    children: [
      infoLabelCell(label, INFO_LABEL_WIDTH),
      infoValueCell(value, INFO_VALUE_WIDTH, 3),
    ],
  });
}
function infoSplitRow(label1, value1, label2, value2) {
  return new TableRow({
    height: { value: INFO_ROW_HEIGHT, rule: HeightRule.ATLEAST },
    children: [
      infoLabelCell(label1, INFO_LABEL_WIDTH),
      infoValueCell(value1, INFO_SPLIT_VALUE_WIDTH),
      infoLabelCell(label2, INFO_SPLIT_LABEL_WIDTH),
      infoValueCell(value2, INFO_SPLIT_VALUE_WIDTH),
    ],
  });
}
function buildInfoTable({ meetingName, datetime, place, facilitator, recorder, attendees }) {
  return new Table({
    width: { size: INFO_LABEL_WIDTH + INFO_VALUE_WIDTH, type: WidthType.DXA },
    columnWidths: [INFO_LABEL_WIDTH, INFO_SPLIT_VALUE_WIDTH, INFO_SPLIT_LABEL_WIDTH, INFO_SPLIT_VALUE_WIDTH],
    alignment: AlignmentType.CENTER,
    rows: [
      infoFullRow('会議名', meetingName),
      infoFullRow('日時', datetime),
      infoFullRow('場所', place),
      infoSplitRow('進行', facilitator, '記録', recorder),
      infoFullRow('参加者', attendees, INFO_PARTICIPANTS_ROW_HEIGHT),
    ],
  });
}

function buildItemSection(label, content) {
  const heading = new Paragraph({
    spacing: { before: 240, after: 100 },
    children: [new TextRun({ text: `【${label}】`, bold: true })],
  });
  const lines = content.split('\n').map((l) => l.trim()).filter(Boolean);
  const bodyParas = lines.length
    ? lines.map((line) => new Paragraph({ children: [new TextRun(line)] }))
    : [new Paragraph({ children: [new TextRun('（該当なし）')] })];
  return [heading, ...bodyParas];
}

/**
 * 会議報告書のWordファイル(Blob)を作る。
 * meta: { department, name, meetingName, datetime, place, facilitator, recorder, attendees }
 * itemOrder / items: generateアクションの結果（GenerationPanelのresultと同じ形）
 */
export async function buildConferenceWordBlob(meta, itemOrder, items) {
  const doc = new Document({
    styles: {
      default: {
        // ここで指定するsizeは、個別に size を指定していないTextRunだけに効く
        // （社会福祉法人表記=18/承認印=16/タイトル=32は個別指定済みのため対象外）。
        // 結果として「報告者」以降（報告者・氏名・会議情報表・協議事項の各項目）が
        // 一律12pt(24half-pt)になる。
        document: { run: { font: BODY_FONT, size: 24 } },
      },
    },
    sections: [{
      properties: {
        // 明示しないとdocxの既定余白(上下左右1440DXA=1インチ)になり、印刷可能幅が
        // 9026DXAしかなく表の幅(9100DXA)をわずかに下回って右側がはみ出す。
        // A4・実際の様式に合わせた余白にして印刷可能幅を10091DXAまで広げる。
        page: {
          size: { width: 11906, height: 16838 }, // A4
          margin: { top: 720, right: 851, bottom: 720, left: 964, header: 851, footer: 992, gutter: 0 },
        },
      },
      children: [
        new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: '（社会福祉法人ザ・ハート・クラブ）', size: 18 })] }),
        new Paragraph({ text: '' }),
        buildStampTable(),
        new Paragraph({ text: '' }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 },
          children: [new TextRun({ text: '会　議　報　告　書', bold: true, size: 32 })],
        }),
        new Paragraph({
          alignment: AlignmentType.RIGHT,
          indent: { right: TABLE_SIDE_GAP },
          children: [new TextRun(`報告者　部署名： ${meta.department || ''}`)],
        }),
        new Paragraph({
          alignment: AlignmentType.RIGHT,
          indent: { right: TABLE_SIDE_GAP },
          spacing: { after: 200 },
          children: [new TextRun(`氏　名： ${meta.name || ''}`)],
        }),
        buildInfoTable(meta),
        ...itemOrder.flatMap((label) => buildItemSection(label, items[label] || '')),
      ],
    }],
  });
  return Packer.toBlob(doc);
}

/** BlobをファイルとしてダウンロードさせるOR（ブラウザのダウンロード機能を使う）。 */
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
